import axios from 'axios';
import {
  Transaction,
  Ticket,
  TicketType,
  TransactionInitiationResponse,
  TransactionInitiationApiResponse,
  TicketTypesApiResponse,
  TransactionStatusApiResponse,
  TransactionTicketApiResponse,
} from '../types';
import { getKioskId } from '../utils/kioskId';

const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    console.log('[API] Using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const backendPort = process.env.REACT_APP_API_PORT || '80';

  const apiUrl = `${protocol}//${hostname}:${backendPort}/api`;
  console.log('[API] Auto-detected API URL:', apiUrl);
  console.log('[API] window.location:', window.location.href);

  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();
console.log('[API] Final API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const ticketTypeService = {
  getTicketTypes: async (): Promise<TicketType[]> => {
    const response = await api.get<TicketTypesApiResponse>('/ticket-types');
    return response.data.data;
  },
};

export const kioskAuthService = {
  activate: async (activationCode: string): Promise<{ uuid: string; name: string; location?: string }> => {
    const response = await api.post('/kiosk/activate', {
      activation_code: activationCode,
    });
    return response.data.data;
  },

  validate: async (uuid: string): Promise<{ valid: boolean; name: string; location?: string }> => {
    const response = await api.post('/kiosk/validate', {
      uuid: uuid,
    });
    return {
      valid: response.data.valid,
      name: response.data.data.name,
      location: response.data.data.location,
    };
  },
};

export const transactionService = {
  initiate: async (ticketType: TicketType): Promise<TransactionInitiationResponse> => {
    const kioskId = getKioskId();
    const response = await api.post<TransactionInitiationApiResponse>('/transaction/initiate', {
      ticket_type_id: ticketType.id,
      kiosk_id: kioskId,
    });

    return {
      transaction_id: response.data.data.transaction_id,
      status: response.data.data.status,
      amount: response.data.data.amount,
      payment_method: response.data.data.payment_method,
      payment_mode: response.data.payment_mode,
      ticket: response.data.data.ticket!,
      payment_url: response.data.payment_url,
      payment_data: response.data.data.payment_data,
      testing_mode: response.data.testing_mode || false,
      ask_email: response.data.ask_email || false,
    };
  },

  getStatus: async (transactionId: string): Promise<Transaction> => {
    const response = await api.get<TransactionStatusApiResponse>(`/transaction/${transactionId}/status`);
    return response.data.data;
  },

  getTicket: async (transactionId: string): Promise<Ticket> => {
    const response = await api.get<TransactionTicketApiResponse>(`/transaction/${transactionId}/ticket`);
    return response.data.data;
  },

  testComplete: async (transactionId: string): Promise<void> => {
    await api.post(`/transaction/${transactionId}/test-complete`);
  },

  updateEmail: async (transactionId: string, email: string): Promise<void> => {
    await api.patch(`/transaction/${transactionId}/email`, { email });
  },
};

export default api;
